import React, { useState } from "react";
import { Switch } from "react-router";
import { Provider as ReduxProvider } from "react-redux";
import getStore from "@utils/redux/store";
import initializeApplication from "@utils/initialization";
import { QuickSilver } from "./test/quickSilver/loader";
import GlobalExitButton from "./components/header/GlobalExitButton";

import "./styles/global.less";
import { getRouterComponents } from "./router";
import { useOnce } from "./hooks/lifecycle";
import getRedirects from "./redirects";
import { PageSetGate } from "./hooks/pageSet";
import { subscribePersistence } from "./utils/redux/persistence";
import { AppInitProvider, AppInitGate } from "./AppInitGate";
import { ErrorMessageProvider } from "./components/error/ErrorMessage";
import { CommonHead } from "./heads/Common";
import { SurveyContextProvider } from "./hooks/survey/context";
import { useNavTracker } from "./hooks/nav-tracker";
import { bootstrapConsent } from "./utils/consent";
import { trackAppInit, trackAppOpened } from "./hooks/tracking";
import { ScrollToTop } from "./components/scroll-to-top/ScrollToTop";
import { useAsyncError } from "./hooks/error";

import { QueryClientProvider } from "react-query";

// Bind concrete implementation to monitoring APIs
import "./utils/monitoring";
import { queryClient } from "./utils/fetch";
import { loadFeatureFlags } from "./pageDefinitions/featureFlags";
import { GlobalStyles } from "./styles/global";
import { measureTask } from "./utils/timing";
import { addMonitoringFlushHooks } from "@utils/monitoring/bus";
import LiveRegions from "./components/accessibility/LiveRegions";
import { AuthProblemModal } from "./components/AuthProblemModal";
import { ThemeProvider } from "./ThemeProvider";
import { apolloClient } from "./api/apolloClient";
import { ApolloProvider } from "@noom/hive-core";
import { GlobalHeader } from "@components/header/GlobalHeader";
import { useTrackHiveCookieMutation } from "./hooks/useTrackHiveCookieMutation";
import { PageSetAuthGuard } from "./utils/pageSetAuthGuard";

loadFeatureFlags();
bootstrapConsent();

const App = () => {
  useNavTracker();
  useTrackHiveCookieMutation();

  const [appInitialized, setAppInitialized] = useState(false);

  const asyncError = useAsyncError();

  useOnce(() => {
    const init = async () => {
      addMonitoringFlushHooks();
      subscribePersistence(getStore());
      trackAppOpened();
      await measureTask("init", initializeApplication);
      // Finally allow application to render.
      setAppInitialized(true);
      trackAppInit();
    };
    init().catch(asyncError);
  });

  return (
    <>
      <CommonHead />
      <GlobalStyles />

      <AppInitProvider value={appInitialized}>
        <QueryClientProvider client={queryClient}>
          <ApolloProvider client={apolloClient}>
            <ReduxProvider store={getStore()} identityFunctionCheck="never">
              <SurveyContextProvider>
                <ErrorMessageProvider>
                  <ThemeProvider>
                    <AppInitGate>
                      <PageSetGate>
                        <PageSetAuthGuard>
                          <GlobalHeader />
                          <ScrollToTop />
                          <Switch>
                            {
                              // Note that we need to do the concat here vs. treating redirect and router components as
                              // their own components as Switch needs the elements to be direct children.
                              getRedirects().concat(getRouterComponents())
                            }
                          </Switch>
                          <AuthProblemModal />
                          <QuickSilver />
                          <GlobalExitButton />
                        </PageSetAuthGuard>
                      </PageSetGate>
                    </AppInitGate>
                  </ThemeProvider>
                </ErrorMessageProvider>
              </SurveyContextProvider>
            </ReduxProvider>
          </ApolloProvider>
        </QueryClientProvider>
      </AppInitProvider>
      <LiveRegions />
    </>
  );
};

export default App;
